import axios from "axios";
import { useAuthParticipent } from "~/store/authParticipants";

export default defineNuxtPlugin(async (nuxtApp) => {
  const { $reportError } = useNuxtApp();

  const subdomain = window.location.hostname.split(".")[0] ?? "app";
  const config = useRuntimeConfig();
  
  const api = axios.create({
    baseURL: config.public.apiBase,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Tenant": subdomain,
    },
  });

  // Fetch CSRF cookie and store initialization state
  nuxtApp.hooks.hook('app:beforeMount', async () => {
    let csrfInitialized = false;
    let csrfPendingPromise: Promise<void> | null = null;
    const auth = useAuth();
    const authParticipent = useAuthParticipent();

    /**
     * Whether this tab has already been sent to the login page.
     *
     * The redirect is the end of the page either way, and there is never a
     * reason to start it twice: a session that ends while the room is open takes
     * out every request in flight at once — the five-second session poll, the
     * document download, an annotation flush — and each of them used to run
     * `navigateTo`, which stops the Nuxt scope and re-assigns `location.href`.
     * The first one is the navigation; the rest are told the truth and nothing
     * more.
     */
    let sessionExpired = false;

    const MAX_CSRF_RETRIES = 5;
    const CSRF_BASE_BACKOFF_MS = 500;
    const CSRF_MAX_BACKOFF_MS = 30000;

    const csrfBackoffDelay = (attempt: number): Promise<void> => {
      const exponential = CSRF_BASE_BACKOFF_MS * Math.pow(2, attempt);
      const jitter = Math.random() * exponential * 0.2;
      const delay = Math.min(exponential + jitter, CSRF_MAX_BACKOFF_MS);
      return new Promise<void>((resolve) => setTimeout(resolve, delay));
    };

    const initializeCSRF = (): Promise<void> => {
      if (csrfInitialized) {
        return Promise.resolve();
      }
      if (csrfPendingPromise !== null) {
        return csrfPendingPromise;
      }

      const attempt = async (attemptsLeft: number): Promise<void> => {
        try {
          await api.get("/sanctum/csrf-cookie");
          csrfInitialized = true;
        } catch (error) {
          if (attemptsLeft <= 1) {
            $reportError("Failed to fetch CSRF cookie after all retries:", error);
            throw error;
          }
          const attemptNumber = MAX_CSRF_RETRIES - attemptsLeft;
          await csrfBackoffDelay(attemptNumber);
          return attempt(attemptsLeft - 1);
        }
      };

      csrfPendingPromise = attempt(MAX_CSRF_RETRIES).finally(() => { csrfPendingPromise = null; });
      return csrfPendingPromise;
    };

    // Initialize after app creation
    await initializeCSRF();

    // Interceptor to ensure CSRF is ready
    api.interceptors.request.use(
      async (config) => {
        if (config.url !== "/sanctum/csrf-cookie") {
          await initializeCSRF();
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle 419 (CSRF token mismatch) responses
    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        try {
          if (error.response?.status === 401 && (auth.user || authParticipent.user)) {
            // A request in flight when the session ID rotates (e.g. right after
            // login regenerates it) can be rejected with the now-stale cookie
            // even though the user is still authenticated. Retry once with the
            // current cookies before treating this as a real logout.
            if (!error.config._authRetried) {
              error.config._authRetried = true;
              await new Promise((resolve) => setTimeout(resolve, 200));
              try {
                return await api.request(error.config);
              } catch (retryError) {
                if ((retryError as any)?.response?.status !== 401) {
                  return Promise.reject(retryError);
                }
                // Falls through to the redirect below on a second real 401.
              }
            }
            // The redirect is started, and the request is still failed.
            //
            // `navigateTo` on an external URL hands back `Promise.resolve()`, so
            // returning it *resolved* the request with `undefined` and every
            // caller went on as though it had an answer: `const { data } = await
            // $axios.get(...)` threw a TypeError about destructuring, and a bare
            // `await $axios.post(...)` looked like a save that had worked.
            //
            // Found while working LWR-26786: a signer's viewer was answered 401
            // for a library that is not its session's to read, and what reached
            // the caller was `undefined` rather than that 401.
            if (!sessionExpired) {
              sessionExpired = true;
              void navigateTo(`${config.public.primaryURL}/login?session_expired=true`, {external: true});
            }

            // Rejected with what came back, so a caller reading `response.status`
            // still sees the 401.
            return Promise.reject(error);
          }
          if (error.response?.status === 419) {
            // Retry up to 5 times with a fresh CSRF token
            const retryCount = error.config._csrfRetryCount ?? 0;
            if (retryCount >= 5) {
              return Promise.reject(error);
            }
            csrfInitialized = false;
            await csrfBackoffDelay(retryCount);
            await initializeCSRF();
            error.config._csrfRetryCount = retryCount + 1;
            return api.request(error.config);
          }
          return Promise.reject(error);
        } catch (handlerError) {
          // Every branch above runs under this catch, not only the CSRF leg it
          // used to name — which is how a defect in the 401 branch stayed
          // invisible. The caller is still owed the answer it actually got.
          $reportError("Failed to handle a failed request:", handlerError);
          return Promise.reject(error);
        }
      }
    );
  });

  return {
    provide: {
      axios: api,
    },
  };
});